import type { PatientInfoValues } from '../types/flow';

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Australian mobile, digits only: 04XXXXXXXX. */
export const AU_MOBILE_PATTERN = /^04\d{8}$/;
export const POSTCODE_PATTERN = /^\d{4}$/;

const PERSONAL_REQUIRED_FIELDS: (keyof PatientInfoValues)[] = [
  'firstName',
  'lastName',
  'dateOfBirth',
  'gender',
  'email',
  'mobile',
  'address',
  'suburb',
  'state',
  'postcode',
  'cultural',
  'referral',
];

const isFilled = (value: unknown): boolean => typeof value === 'string' && value.trim() !== '';

/**
 * The Personal & Contact tab is done: every field filled and well-formed.
 * Decides whether the patient may move on, so it reads the values rather than
 * the form's error state, which is lost for fields on the other tab.
 */
export const isPersonalInfoComplete = (values: PatientInfoValues): boolean => (
  PERSONAL_REQUIRED_FIELDS.every((field) => isFilled(values[field]))
  && EMAIL_PATTERN.test(values.email)
  && AU_MOBILE_PATTERN.test(values.mobile)
  && POSTCODE_PATTERN.test(values.postcode)
);

const isCardComplete = (values: PatientInfoValues): boolean => {
  switch (values.medicareType) {
    case 'medicare':
      return isFilled(values.medicareNumber) && isFilled(values.medicareRef) && isFilled(values.medicareExpiry);
    case 'dva':
      return isFilled(values.dvaNumber) && isFilled(values.dvaCardType);
    case 'none':
      return true;
    default:
      return false;
  }
};

/** The Emergency & Medicare tab is done. */
export const isEmergencyMedicareComplete = (values: PatientInfoValues): boolean => (
  isFilled(values.emergencyName)
  && isFilled(values.emergencyRelationship)
  && AU_MOBILE_PATTERN.test(values.emergencyMobile)
  && isCardComplete(values)
);
